import { IBrand } from "@faire/web-api/indigofair/data/IBrand";
import { IProduct } from "@faire/web-api/indigofair/data/IProduct";
import { useStrictLocalization } from "@faire/web/common/localization";
import { isPrerender } from "@faire/web/common/user-agent/isPrerender";
import { Column } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { useEffectEvent } from "@faire/web/ui/hooks/useEffectEvent";
import { observer } from "mobx-react";
import React from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";

import { LoadingProductTileSkeleton } from "@faire/retailer/components/LoadingProductTileSkeleton/LoadingProductTileSkeleton";
import { PromotedBadge } from "@faire/retailer/features/ADS/PromotedBadge";
import { shouldShowPromotedBadge } from "@faire/retailer/features/ADS/util";
import { getAltTag } from "@faire/retailer/features/Marketplace/utils";
import { Container as MarketplaceProductContainer } from "@faire/retailer/features/MarketplaceItems/MarketplaceProduct/Container";
import {
  ILeanProductTile,
  ILeanProductTileWithBrand,
} from "@faire/retailer/features/MarketplaceItems/MarketplaceProduct/LeanProtos/ILeanProductTile";
import { MarketplaceProduct } from "@faire/retailer/features/MarketplaceItems/MarketplaceProduct/MarketplaceProduct";
import { ProductLinkProps } from "@faire/retailer/features/MarketplaceItems/ProductTile";
import { useProductTileClickImpressions } from "@faire/retailer/hooks/ProductTileImpression/useProductTileClickImpressions";
import { generateProductLinkPropsForLoggedOutRetailer } from "@faire/retailer/lib/generateProductLinkPropsForLoggedOutRetailer";
import { isAdmin } from "@faire/retailer/lib/isAdmin";
import { useIsLoggedInRetailer } from "@faire/retailer/lib/isLoggedInRetailer";
import { brandPage } from "@faire/retailer/paths";
import { ProductPagePaths } from "@faire/retailer/paths/ProductPage";
import { MarketplaceProductList } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/MainContent/__internal__/ProductOrBrandGrid/__internal__/ProductFirst/__internal__/MarketplaceProductList/MarketplaceProductList";

const TileBase: React.FC<
  {
    tileData: ILeanProductTileWithBrand | undefined;
    product?: ILeanProductTile.ILeanProduct;
    tileIndex?: number;
  } & Pick<
    MarketplaceProductList.IProps,
    "title" | "pageType" | "filterKeys" | "category" | "showVerticalImagery"
  >
> = ({
  product,
  tileData,
  title,
  pageType,
  filterKeys,
  category,
  showVerticalImagery,
  tileIndex,
}) => {
  const isLoggedInRetailer = useIsLoggedInRetailer();
  const showPromotedBadge = shouldShowPromotedBadge(
    tileData?.product?.is_promoted,
    tileData?.product?.promoted_by ?? []
  );
  const {
    recordProductTileRightClickEvent,
    recordProductTileMiddleClickEvent,
  } = useProductTileClickImpressions();

  const { strictLocalize } = useStrictLocalization();
  const location = useLocation();
  const getProductLinkPropsFromProductTile = (
    productTile?: ILeanProductTile
  ): ProductLinkProps | undefined => {
    const product = productTile?.product;
    if (!product?.token || !productTile) {
      return undefined;
    }

    const productPageLink = ProductPagePaths.index(product.token, {
      refF: filterKeys,
      refC: category?.navigationPath,
      productOptionToken: productTile.selected_product_option_token,
      impressionId: product.impression_id,
    });

    if (!isLoggedInRetailer && !isAdmin() && !isPrerender()) {
      return generateProductLinkPropsForLoggedOutRetailer({
        productToken: product.token,
        brandToken: product.brand_token,
        refF: filterKeys,
        refC: category?.navigationPath,
        productOptionToken: productTile.selected_product_option_token,
        location,
      });
    }

    return {
      productUrl: productPageLink,
    };
  };

  const getBrandUrlFromProductTile = (brand?: Pick<IBrand, "token">) => {
    if (!brand) {
      return undefined;
    }

    return brandPage(brand.token!, {
      page: 1,
      refC: category?.navigationPath,
      refF: filterKeys,
      refP: product?.token ? [product.token] : undefined,
      sortingHint: true,
      impressionId: product?.impression_id,
    });
  };

  const handleOnAuxClick = useEffectEvent(
    (event: React.MouseEvent<HTMLElement>) => {
      if (event.button === 1) {
        recordProductTileMiddleClickEvent();
      } else if (event.button === 2) {
        recordProductTileRightClickEvent();
      }
    }
  );

  if (tileData === undefined) {
    return <LoadingProductTileSkeleton />;
  }

  return (
    <TileWrapper
      $enableSearchResultCleanup={isLoggedInRetailer}
      onAuxClick={handleOnAuxClick}
    >
      <MarketplaceProduct
        altTag={getAltTag(
          {
            title,
            pageType,
            productName: tileData?.product?.name,
          },
          strictLocalize
        )}
        showUserDrivenFeedbackTooltip
        productTile={tileData}
        brand={tileData?.brand}
        generateProductLinkProps={() =>
          getProductLinkPropsFromProductTile(tileData)
        }
        generateBrandUrl={() => getBrandUrlFromProductTile(tileData?.brand)}
        showVerticalImagery={showVerticalImagery}
        shouldShowPromotedBadge={showPromotedBadge}
        recordAdImpression
        tileIndex={tileIndex}
      />
      <MaybeShowLoggedOutPromotedBadge
        showPromotedBadge={showPromotedBadge}
        promotedBy={tileData?.product?.promoted_by}
      />
    </TileWrapper>
  );
};

export const Tile = observer(TileBase);

const MaybeShowLoggedOutPromotedBadge: React.FC<{
  showPromotedBadge: boolean;
  promotedBy: Array<keyof typeof IProduct.PromotedType> | undefined;
}> = ({ showPromotedBadge }) => {
  const isLoggedInRetailer = useIsLoggedInRetailer();
  if (isLoggedInRetailer) {
    return null;
  }

  if (showPromotedBadge) {
    return <PromotedBadge />;
  }

  return <Spacer size="3x" />;
};

const TileWrapper = styled(Column)<{
  $enableSearchResultCleanup: boolean;
}>`
  min-width: 0;
  width: 100%;
  padding: ${({ $enableSearchResultCleanup }) =>
    $enableSearchResultCleanup ? 0 : getSpacing("1x", 4, 0)};

  ${responsiveMediaQueries.tabletAndAbove} {
    padding: ${({ $enableSearchResultCleanup }) =>
      $enableSearchResultCleanup ? 0 : getSpacing("1x", "1x", 0)};
  }

  & ${MarketplaceProductContainer} {
    flex-grow: 1;
  }
`;
